/* eslint-disable @typescript-eslint/no-explicit-any -- Maps JS API is script-injected, untyped by design */
import { useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import type { PublicSpot } from '~pages/Group/query';
const KEY = process.env.GOOGLE_MAPS_API_KEY;

// Ported from the Flutter app's map_style.dart.
const MAP_STYLE = [{
  featureType: 'landscape.natural',
  elementType: 'geometry.fill',
  stylers: [{
    visibility: 'on'
  }, {
    color: '#e0efe1'
  }]
}, {
  featureType: 'poi',
  elementType: 'geometry.fill',
  stylers: [{
    visibility: 'on'
  }, {
    hue: '#1900ff'
  }, {
    color: '#e0efe1'
  }]
}, {
  featureType: 'road',
  elementType: 'geometry',
  stylers: [{
    lightness: 100
  }, {
    visibility: 'simplified'
  }]
}, {
  featureType: 'poi',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'transit',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'water',
  elementType: 'all',
  stylers: [{
    color: '#055574'
  }]
}, {
  featureType: 'all',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}];
let mapsPromise: Promise<any> | null = null;
function loadMaps(): Promise<any> {
  const w = window as any;
  if (w.google?.maps) return Promise.resolve(w.google.maps);
  if (!mapsPromise) {
    mapsPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}`;
      s.async = true;
      s.onload = () => resolve(w.google.maps);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return mapsPromise;
}
export function SpotMap({
  spot
}: {
  spot: PublicSpot;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Skip on mobile (the map is desktop-only) and when no key is configured —
    // avoids loading the Maps API where it isn't shown.
    if (!KEY || !window.matchMedia('(min-width: 60rem)').matches) return;
    let cancelled = false;
    const center = {
      lat: spot.coordinates.latitude,
      lng: spot.coordinates.longitude
    };
    loadMaps().then(maps => {
      if (cancelled || !ref.current) return;
      const map = new maps.Map(ref.current, {
        center,
        zoom: 15,
        disableDefaultUI: true,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        clickableIcons: false,
        styles: MAP_STYLE
      });
      new maps.Marker({
        position: center,
        map,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: '#00E676',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3
        }
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [spot.coordinates.latitude, spot.coordinates.longitude]);
  return <div {...stylex.props(styles.wrap)}>
      <div ref={ref} {...stylex.props(styles.map)} />
    </div>;
}